import React from 'react';

export type ActionButtonVariant = 'primary' | 'secondary' | 'disabled';

/*
 * `type` is exposed because some hosts (login form, register form, change
 * password form, edit-profile form) render the button inside an Angular form
 * with `ng-submit`. Those forms relied on a native `type="submit"` button to
 * fire submission both via click and via Enter-in-input. The default stays
 * `'button'` so existing call sites (classes day/list view, memberships)
 * keep working without changes.
 */
export type ActionButtonType = 'button' | 'submit';

/*
 * `leadingIcon` / `trailingIcon` are intentionally lenient strings rather
 * than a tagged union so they're trivial to pass through `<react-directive
 * data="…">` from Angular controllers.
 *
 * Two shapes are supported per slot:
 *   1. A known SVG key (currently just `'manage-membership'`). The component
 *      renders the predefined inline SVG so the icon survives the strangulation
 *      from Angular without needing to ship raw HTML through the data binding.
 *   2. Anything else is treated as an icon-font class name (e.g.
 *      `'icon-arrow-right'`). The component renders `<i class={value} />` —
 *      same convention the legacy Angular templates used.
 *
 * Both are optional; ActionButtons without icons keep their existing
 * label-only layout (no flex, centered text) so nothing changes for the
 * dozens of existing call sites.
 */
export type ActionButtonData = {
  label?: string;
  id?: string;
  dataTestId?: string;
  variant?: ActionButtonVariant;
  ariaLabel?: string;
  type?: ActionButtonType;
  leadingIcon?: string;
  trailingIcon?: string;
};

type Props = {
  data: ActionButtonData;
};

const VARIANT_CLASS: Record<ActionButtonVariant, string> = {
  primary: 'action-button',
  secondary: 'action-button action-button--secondary',
  disabled: 'action-button action-button--disabled',
};

function renderIcon(value: string | undefined, slotClassName: string) {
  if (!value) {
    return null;
  }

  if (value === 'manage-membership') {
    return (
      <span className={slotClassName} aria-hidden="true">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="12" className="icon-manage-membership-bg" />
          <path
            d="M12 7C9.24 7 7 9.24 7 12C7 14.76 9.24 17 12 17C14.76 17 17 14.76 17 12C17 9.24 14.76 7 12 7ZM9.535 15.14C9.75 14.69 11.06 14.25 12 14.25C12.94 14.25 14.255 14.69 14.465 15.14C13.785 15.68 12.93 16 12 16C11.07 16 10.215 15.68 9.535 15.14ZM15.18 14.415C14.465 13.545 12.73 13.25 12 13.25C11.27 13.25 9.535 13.545 8.82 14.415C8.31 13.745 8 12.91 8 12C8 9.795 9.795 8 12 8C14.205 8 16 9.795 16 12C16 12.91 15.69 13.745 15.18 14.415V14.415ZM12 9C11.03 9 10.25 9.78 10.25 10.75C10.25 11.72 11.03 12.5 12 12.5C12.97 12.5 13.75 11.72 13.75 10.75C13.75 9.78 12.97 9 12 9ZM12 11.5C11.585 11.5 11.25 11.165 11.25 10.75C11.25 10.335 11.585 10 12 10C12.415 10 12.75 10.335 12.75 10.75C12.75 11.165 12.415 11.5 12 11.5Z"
            className="icon-manage-membership"
          />
        </svg>
      </span>
    );
  }

  return <i className={`${slotClassName} ${value}`} aria-hidden="true" />;
}

export function ActionButton({ data }: Props) {
  if (!data) {
    return null;
  }

  const {
    label,
    id,
    dataTestId,
    variant = 'primary',
    ariaLabel,
    type = 'button',
    leadingIcon,
    trailingIcon,
  } = data;
  const isDisabled = variant === 'disabled';
  const baseClassName = VARIANT_CLASS[variant] ?? VARIANT_CLASS.primary;
  const hasIcons = !!(leadingIcon || trailingIcon);
  const className = hasIcons ? `${baseClassName} action-button--with-icons` : baseClassName;
  const buttonType: ActionButtonType = type === 'submit' ? 'submit' : 'button';

  return (
    <button
      type={buttonType}
      id={id || undefined}
      data-testid={dataTestId || undefined}
      aria-label={ariaLabel || undefined}
      aria-disabled={isDisabled || undefined}
      disabled={isDisabled}
      className={className}
    >
      {renderIcon(leadingIcon, 'action-button__leading-icon')}
      {hasIcons ? <span className="action-button__label">{label}</span> : label}
      {renderIcon(trailingIcon, 'action-button__trailing-icon')}
    </button>
  );
}
